import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  Inventory2Outlined,
  RouteOutlined,
  SchemaOutlined,
  WarehouseOutlined,
} from '@mui/icons-material';

import { Header } from '@/__core/layout';
import MainListModule from '@/__core/layout/module/MainListModule';
import { LabeledSelect, LabeledTextField } from '@/__core/components/LabeledField';
import UnsavedChangesDialog from '@/__core/helpers/components/UnsavedChangesDialog';
import { TabData } from '@/__core/types/TabData';
import { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import {
  getInventorySection,
  getInventoryView,
  INVENTORY_CONTRACTS,
  INVENTORY_SECTIONS,
  INVENTORY_STATUSES,
} from '@/modules/inventory/config/inventoryConfig';
import { useTabStorage } from '@/modules/inventory/hooks/useTabStorage';
import { InventoryFilterState, InventoryRecord, InventorySectionKey, InventoryViewKey } from '@/modules/inventory/types';
import { RootState } from '@/setup/redux/RootReducer';

const defaultTabs: TabData[] = [
  { id: 0, title: 'Danh sách', type: 'list', closable: false, createdAt: Date.now() },
  { id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() },
];

const makeDefaultFormData = (viewKey: InventoryViewKey, record?: InventoryRecord) => {
  const view = getInventoryView(viewKey);
  return view.formFields.reduce<Record<string, string | number>>((accumulator, field) => {
    const currentValue = record?.[field.key];
    accumulator[field.key] = typeof currentValue === 'number' ? currentValue : currentValue || '';
    return accumulator;
  }, {});
};

interface InventoryModuleProps {
  section: InventorySectionKey;
}

export default function InventoryModule({ section }: InventoryModuleProps) {
  const navigate = useNavigate();
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const companies = useSelector((state: RootState) => state.auth.companies);

  const sectionConfig = getInventorySection(section);
  const initialViewKey = sectionConfig.views[0];
  const [activeViewKey, setActiveViewKey] = useState<InventoryViewKey>(initialViewKey);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [dirtyTabIds, setDirtyTabIds] = useState<Record<number, boolean>>({});

  const companyOptions: CompanyData[] = companies.length > 0 ? companies : [];
  const storeOptions: Array<{ value: string; label: string }> = [];
  const inventorySummaryBySection: Record<string, Array<{ label: string; value: string; tone?: string }>> = {};

  const [filters, setFilters] = useState<InventoryFilterState>({
    companyId: selectedCompany?.company_id || companyOptions[0]?.company_id || '',
    storeId: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: '',
    search: '',
  });

  useEffect(() => {
    setActiveViewKey(initialViewKey);
  }, [initialViewKey]);

  const { tabs, activeTab, setActiveTab, addTab, closeTab, updateTab, resetTabs } = useTabStorage({
    storageKey: `inventory_module_tabs_${section}`,
    defaultTabs,
    maxTabs: 12,
  });

  const displayTabs = useMemo(
    () => tabs.map((tab, index) => ({ ...tab, active: index === activeTab })),
    [tabs, activeTab]
  );

  const activeView = getInventoryView(activeViewKey);
  const activeTabData = tabs[activeTab];
  const isActiveTabDirty = activeTabData ? Boolean(dirtyTabIds[activeTabData.id]) : false;

  const currentRows = useMemo(() => [] as InventoryRecord[], []);

  const runGuardedAction = (action: () => void) => {
    if (isActiveTabDirty) {
      setPendingAction(() => action);
      setUnsavedDialogOpen(true);
      return;
    }

    action();
  };

  const handleConfirmDiscard = () => {
    setUnsavedDialogOpen(false);
    if (activeTabData) {
      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
    }
    pendingAction?.();
    setPendingAction(null);
  };

  const handleCancelDiscard = () => {
    setUnsavedDialogOpen(false);
    setPendingAction(null);
  };

  const handleTabChange = (_event: SyntheticEvent, newIndex: number) => {
    if (newIndex >= tabs.length || tabs[newIndex]?.id === 10) {
      return;
    }

    runGuardedAction(() => setActiveTab(newIndex));
  };

  const handleCloseTab = (tabId: number) => {
    const tabToClose = tabs.find((tab) => tab.id === tabId);
    const action = () => {
      setDirtyTabIds((current) => ({ ...current, [tabId]: false }));
      closeTab(tabId);
    };

    if (tabToClose && dirtyTabIds[tabId]) {
      setPendingAction(() => action);
      setUnsavedDialogOpen(true);
      return;
    }

    action();
  };

  const handleAddTab = () => {
    runGuardedAction(() => {
      addTab({
        id: Date.now(),
        title: activeView.createLabel,
        type: 'new-inventory-record',
        closable: true,
        entityType: activeView.key,
        metadata: {
          viewKey: activeView.key,
          formData: makeDefaultFormData(activeView.key),
        },
        createdAt: Date.now(),
      });
    });
  };

  const handleSelectSection = (targetSection: InventorySectionKey) => {
    const target = getInventorySection(targetSection);
    runGuardedAction(() => {
      resetTabs();
      navigate(target.path);
    });
  };

  const handleSelectView = (viewKey: InventoryViewKey) => {
    runGuardedAction(() => {
      setActiveViewKey(viewKey);
      resetTabs();
    });
  };

  const handleEditRecord = (record: InventoryRecord) => {
    addTab({
      id: Date.now(),
      title: `${activeView.updateLabel}: ${record.code}`,
      type: 'update-inventory-record',
      closable: true,
      entityId: record.id,
      entityType: activeView.key,
      metadata: {
        viewKey: activeView.key,
        record,
        formData: makeDefaultFormData(activeView.key, record),
      },
      createdAt: Date.now(),
    });
  };

  const handleCompanyChange = (companyId: string) => {
    setFilters((current) => ({ ...current, companyId }));
  };

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {(inventorySummaryBySection[section] || []).map((card) => (
        <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={card.label}>
          <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
            <Typography variant='body2' sx={{ color: '#64748b', mb: 1 }}>
              {card.label}
            </Typography>
            <Typography variant='h4' sx={{ fontWeight: 700, color: card.tone }}>
              {card.value}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  const renderDashboardContent = () => (
    <Stack spacing={2}>
      <Alert severity='info'>Danh sách sẽ hiển thị khi module được kết nối API. Hiện chưa có dữ liệu mock.</Alert>
      {renderSummaryCards()}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
              <RouteOutlined fontSize='small' />
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                Route roadmap inventory
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              {INVENTORY_SECTIONS.map((item) => (
                <Box key={item.key} sx={{ p: 1.5, borderRadius: 2, backgroundColor: item.key === section ? '#eff6ff' : '#f8fafc' }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    {item.path}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
              <SchemaOutlined fontSize='small' />
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                API contract mức 1
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              {INVENTORY_CONTRACTS[section].map((contract) => (
                <Card key={contract.name} variant='outlined' sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction='row' justifyContent='space-between' spacing={1} sx={{ mb: 1 }}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>{contract.name}</Typography>
                      <Chip size='small' label={contract.method} color='primary' />
                    </Stack>
                    <Typography variant='body2' sx={{ fontFamily: 'monospace', mb: 1 }}>{contract.path}</Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>{contract.purpose}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Fields: {contract.fields.join(', ')}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );

  const renderTable = () => (
    <TableContainer component={Paper} variant='outlined' sx={{ borderRadius: 3, maxHeight: '100%' }}>
      <Table stickyHeader size='small'>
        <TableHead>
          <TableRow>
            {activeView.columns.map((column) => (
              <TableCell key={column.key} align={column.align || 'left'} sx={{ fontWeight: 700, minWidth: column.minWidth }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {currentRows.map((row) => (
            <TableRow hover key={row.id} onClick={() => handleEditRecord(row)} sx={{ cursor: 'pointer' }}>
              {activeView.columns.map((column) => (
                <TableCell key={`${row.id}-${column.key}`} align={column.align || 'left'}>
                  {column.key === 'status' ? <Chip size='small' label={row[column.key] || '-'} /> : row[column.key] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {currentRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={Math.max(activeView.columns.length, 1)}>
                <Box sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                  Chưa có dữ liệu phù hợp với bộ lọc hiện tại.
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderContractCards = () => (
    <Stack spacing={1.5}>
      {INVENTORY_CONTRACTS[section].map((contract) => (
        <Paper key={contract.name} variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction='row' justifyContent='space-between' spacing={1} sx={{ mb: 1 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>{contract.name}</Typography>
            <Chip size='small' label={contract.method} />
          </Stack>
          <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>{contract.path}</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>{contract.purpose}</Typography>
        </Paper>
      ))}
    </Stack>
  );

  const renderFormTab = () => {
    const viewKey = (activeTabData?.metadata?.viewKey as InventoryViewKey) || activeViewKey;
    const formView = getInventoryView(viewKey);
    const formData = (activeTabData?.metadata?.formData || makeDefaultFormData(viewKey)) as Record<string, string | number>;

    const updateFormField = (fieldKey: string, value: string) => {
      if (!activeTabData) {
        return;
      }

      const nextFormData = {
        ...formData,
        [fieldKey]: value,
      };

      updateTab(activeTabData.id, {
        metadata: {
          ...activeTabData.metadata,
          formData: nextFormData,
        },
      });

      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
    };

    const handleSaveMock = () => {
      if (!activeTabData) {
        return;
      }

      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
      setSnackbarMessage(`${formView.title}: đã lưu mock draft để review UX/API contract.`);
    };

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2} sx={{ mb: 3 }}>
              <Box>
                <Typography variant='h5' sx={{ fontWeight: 700 }}>{activeTabData?.title}</Typography>
                <Typography variant='body2' color='text.secondary'>{formView.description}</Typography>
              </Box>
              <Stack direction='row' spacing={1}>
                <Chip icon={<WarehouseOutlined />} label={`Company: ${selectedCompany?.company_name || 'Mock Company'}`} />
                <Chip label='Mock form' color='warning' />
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              {formView.formFields.map((field) => (
                <Grid size={{ xs: 12, md: field.type === 'textarea' ? 12 : 6 }} key={field.key}>
                  {field.type === 'select' ? (
                    <LabeledSelect
                      label={field.label}
                      value={`${formData[field.key] || ''}`}
                      onChange={(event) => updateFormField(field.key, event.target.value as string)}
                      options={(field.options || []).map((option) => ({ id: option.value, name: option.label }))}
                      fullWidth
                    />
                  ) : (
                    <LabeledTextField
                      label={field.label}
                      value={`${formData[field.key] || ''}`}
                      onChange={(event) => updateFormField(field.key, event.target.value)}
                      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      required={field.required}
                      helperText={field.helperText}
                      fullWidth
                      multiline={field.type === 'textarea'}
                      minRows={field.type === 'textarea' ? 3 : undefined}
                      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2}>
              <Typography variant='body2' color='text.secondary'>
                Form này đang phục vụ review Phase 1: field naming, validation sơ bộ và hành vi tab/dirty state.
              </Typography>
              <Stack direction='row' spacing={1}>
                <Button variant='outlined' onClick={() => setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }))}>
                  Mark clean
                </Button>
                <Button variant='contained' onClick={handleSaveMock}>
                  Lưu mock draft
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>API contract liên quan</Typography>
            {renderContractCards()}
          </Paper>
        </Stack>
      </Box>
    );
  };

  const renderListTab = () => (
    <Box sx={{ p: 2.5 }}>
      {section === 'dashboard' ? (
        renderDashboardContent()
      ) : (
        <>
          {renderSummaryCards()}
          <MainListModule
            leftSidebar={
              <Box sx={{ p: 1.5 }}>
                <Typography variant='subtitle2' sx={{ px: 1, pb: 1, fontWeight: 700, color: '#475569' }}>
                  Nhóm chức năng
                </Typography>
                <List dense>
                  {INVENTORY_SECTIONS.map((item) => (
                    <ListItemButton
                      key={item.key}
                      selected={item.key === section}
                      onClick={() => handleSelectSection(item.key)}
                      sx={{ borderRadius: 2, mb: 0.5 }}
                    >
                      <ListItemText primary={item.navLabel} secondary={item.description} />
                    </ListItemButton>
                  ))}
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant='subtitle2' sx={{ px: 1, pb: 1, fontWeight: 700, color: '#475569' }}>
                  Màn hình trong nhóm
                </Typography>
                <List dense>
                  {sectionConfig.views.map((viewKey) => {
                    const view = getInventoryView(viewKey);
                    return (
                      <ListItemButton
                        key={viewKey}
                        selected={viewKey === activeViewKey}
                        onClick={() => handleSelectView(viewKey)}
                        sx={{ borderRadius: 2, mb: 0.5 }}
                      >
                        <ListItemText primary={view.title} secondary={view.description} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            }
            toolbar={
              <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems={{ xs: 'stretch', xl: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='h6' sx={{ fontWeight: 700 }}>{activeView.title}</Typography>
                    <Typography variant='body2' color='text.secondary'>{activeView.description}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                    <TextField
                      select
                      size='small'
                      label='Công ty'
                      value={filters.companyId}
                      onChange={(event) => handleCompanyChange(event.target.value)}
                      sx={{ minWidth: 220 }}
                    >
                      {companyOptions.map((company) => (
                        <MenuItem key={company.company_id} value={company.company_id}>
                          {company.company_name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size='small'
                      label='Kho'
                      value={filters.storeId}
                      onChange={(event) => setFilters((current) => ({ ...current, storeId: event.target.value }))}
                      sx={{ minWidth: 170 }}
                    >
                      {storeOptions.map((store) => (
                        <MenuItem key={store.value} value={store.value}>{store.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size='small'
                      label='Trạng thái'
                      value={filters.status}
                      onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                      sx={{ minWidth: 170 }}
                    >
                      {INVENTORY_STATUSES.map((status) => (
                        <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size='small'
                      type='date'
                      label='Từ ngày'
                      value={filters.dateFrom}
                      onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size='small'
                      type='date'
                      label='Đến ngày'
                      value={filters.dateTo}
                      onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size='small'
                      label='Tìm kiếm'
                      value={filters.search}
                      onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                    />
                  </Stack>
                </Stack>
              </Paper>
            }
            dataTable={
              <Stack spacing={2} sx={{ height: '100%' }}>
                {renderTable()}
                <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>Contract preview</Typography>
                  {renderContractCards()}
                </Paper>
              </Stack>
            }
          />
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)' }}>
      <Header
        tabs={displayTabs}
        activeTab={activeTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='reports'
      />

      <Box sx={{ px: 3, pt: 2 }}>
        <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #dbeafe', backgroundColor: 'rgba(255,255,255,0.85)' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent='space-between' spacing={2}>
            <Box>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                <Inventory2Outlined color='primary' />
                <Typography variant='h5' sx={{ fontWeight: 800 }}>{sectionConfig.title}</Typography>
              </Stack>
              <Typography variant='body1' color='text.secondary'>
                {sectionConfig.description}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Chip label={`Company scope: ${selectedCompany?.company_name || 'Mock company'}`} color='primary' variant='outlined' />
              <Chip label={`View: ${activeView.title}`} variant='outlined' />
              <Button variant='contained' startIcon={<AddCircleOutline />} onClick={handleAddTab}>
                {activeView.createLabel}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {activeTabData?.type === 'list' ? renderListTab() : renderFormTab()}

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3000} onClose={() => setSnackbarMessage(null)}>
        <Alert severity='success' onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <UnsavedChangesDialog
        open={unsavedDialogOpen}
        onClose={handleCancelDiscard}
        onConfirmLeave={handleConfirmDiscard}
      />
    </Box>
  );
}